import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  LoadingSpinner,
  DataLoadingWrapper,
  MultiSectionLayout,
} from "@jugl-web/ui-kit";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import { useTranslations } from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useVoiceAccount } from "../../../../hooks/useVoiceAccount";
import { useUpdateVoiceSettings } from "../../hooks/useUpdateVoiceSettings";
import { VoiceGeneralFormValues } from "../../types";
import {
  callDurationMinutesToSeconds,
  getVoiceGeneralFormValues,
} from "../../utils/getVoiceFormValues";
import { AIAgentVoiceSection } from "../AIAgentVoiceSection";
import { AIInstructionsSection } from "../AIInstructionsSection";
import { CallsLimitSection } from "../CallsLimitSection";
import { VoiceHeaderSection } from "../VoiceHeaderSection";
import { WelcomeMessageSection } from "../WelcomeMessageSection";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export const GeneralTab: React.FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { account } = useVoiceAccount();
  const { updateVoiceSettings } = useUpdateVoiceSettings();
  const { requestNavigation, registerDirtySource } =
    useNavigationGuardContext();
  const { isSubmitting, setIsSubmitting } = useFormSubmittingState();
  const { integrationsApi } = useRestApiProvider();
  const {
    isLoading: isAgentConfigLoading,
    isError: isAgentConfigError,
    refetch: refetchAgentConfig,
  } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
  });

  const form = useForm<VoiceGeneralFormValues>({
    defaultValues: getVoiceGeneralFormValues(account),
    reValidateMode: "onBlur",
  });

  const { handleSubmit, reset, formState } = form;

  const resetForm = useCallback(() => {
    reset(getVoiceGeneralFormValues(account));
  }, [account, reset]);

  const handleSaveChanges = useCallback(
    async (formData: VoiceGeneralFormValues) => {
      try {
        setIsSubmitting(true);

        const isUpdated = await updateVoiceSettings({
          metadata: {
            greeting: formData.welcome_message.trim(),
            instructions: formData.instructions.trim(),
            use_base_instructions: formData.use_base_instructions,
            voice: formData.voice,
            call_limiter: {
              day_limit: formData.daily_calls_limit,
              max_duration: callDurationMinutesToSeconds(
                formData.max_call_duration_min
              ),
              active_calls: formData.max_concurrent_calls,
            },
          },
        });

        if (isUpdated) {
          reset(formData);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [reset, setIsSubmitting, updateVoiceSettings]
  );

  useEffect(() => {
    return registerDirtySource("twilio-voice-general-tab", formState.isDirty);
  }, [formState.isDirty, registerDirtySource]);

  return (
    <FormProvider {...form}>
      <MultiSectionLayout
        className="h-full"
        classNames={{
          content: "overflow-y-auto",
        }}
        footer={
          formState.isDirty ? (
            <div className="flex gap-4 border-0 border-t border-solid border-dark-100 bg-white px-8 py-4 max-lg:px-4">
              <Button
                variant="tertiary"
                size="md"
                className="w-30"
                onClick={() => requestNavigation(resetForm)}
                isDisabled={isSubmitting}
              >
                {t({
                  id: "common.cancel",
                  defaultMessage: "Cancel",
                })}
              </Button>
              <Button
                variant="primary"
                size="md"
                className="w-75"
                onClick={handleSubmit(handleSaveChanges)}
                isDisabled={isSubmitting}
                iconEnd={
                  isSubmitting ? (
                    <LoadingSpinner size="xs" />
                  ) : (
                    <ArrowRightIcon />
                  )
                }
              >
                {t({
                  id: "common.save",
                  defaultMessage: "Save",
                })}
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 p-8 max-lg:gap-4 max-lg:p-4">
          <DataLoadingWrapper
            isLoading={isAgentConfigLoading}
            isError={isAgentConfigError}
            onRetry={refetchAgentConfig}
          >
            <VoiceHeaderSection />
            <AIInstructionsSection />
            <WelcomeMessageSection />
            <AIAgentVoiceSection />
            <CallsLimitSection />
          </DataLoadingWrapper>
        </div>
      </MultiSectionLayout>
    </FormProvider>
  );
};
